import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/ReportsModel";
import MonthlyReportsTable from "./MonthlyReportsTable";
import PageHeader from "../../common/PageHeader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

export default function DailyReports() {
    const module = {
        title: 'Daily Reports',
        singularTitle: 'Daily Report',
        key: 'monthly_reports',
        route: 'daily-reports',
        primaryCol: ''
    };

    const { state } = useAuth();
    const user_data = state.user_data;
    const navigate = useNavigate();

    const [selectedDate, setSelectedDate] = useState(() => {
        return new Date().toISOString().split("T")[0]; // default: today
    });
    const [listData, setListData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [cacheTime, setCacheTime] = useState([]);

    useEffect(() => {
        fetchData(selectedDate);
    }, [selectedDate]);

    const fetchData = async (date) => {
        setLoading(true);
        try {
            const apiParam = {
                date, // backend expects 'date'
                user_token: user_data.user_token,
            };
            const response = await model.getMonthlyReport(apiParam);
            if (typeof response === "undefined") {
                navigate("/re-login");
                return;
            }

            setListData(response[module.key] || []);
            setTotalRows(response.pagination?.total_rows || 0);
            if (response.time) setCacheTime(response.time);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = async () => {
        if (!listData || listData.length === 0) {
            toast.warning("No data to export");
            return;
        }

        const fileName = `Daily_Reports_${selectedDate}.xlsx`;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Daily Report");

        // Define headers
        const headers = [
            { header: "Client Name", key: "client_name_with_gstin" },
            { header: "Active Positions", key: "active_positions" },
            { header: "On Hold", key: "on_hold_positions" },
            { header: "Closed", key: "closed_positions" },
            { header: "Total Submissions", key: "total_submissions" },
            { header: "Total Interviews", key: "total_interviews" },
            { header: "No Shows", key: "no_show_count" },
        ];

        worksheet.columns = headers;

        listData.forEach(row => {
            worksheet.addRow({
                client_name_with_gstin: row.client_name_with_gstin,
                active_positions: Number(row.active_positions) || 0,
                on_hold_positions: Number(row.on_hold_positions) || 0,
                closed_positions: Number(row.closed_positions) || 0,
                total_submissions: Number(row.total_submissions) || 0,
                total_interviews: Number(row.total_interviews) || 0,
                no_show_count: Number(row.no_show_count) || 0,
            });
        });

        // Style header
        worksheet.getRow(1).eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, fileName);
    };

    return (
        <AdminLayout>
            <Helmet>
                <title>{module.title} - Digital CMS</title>
            </Helmet>

            <div className="main-content app-content">
                <div className="container-fluid">
                    <PageHeader module={module} />

                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label">Select Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={selectedDate}
                                max={new Date().toISOString().split("T")[0]} // block future dates
                                onChange={e => setSelectedDate(e.target.value)}
                            />
                        </div>

                        {listData.length > 0 && (
                            <div className="col-md-3 mt-4">
                                <button className="btn btn-primary" onClick={exportToExcel}>
                                    Download Excel
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card custom-card">
                                <div className="card-body table-responsive">
                                    <MonthlyReportsTable
                                        module={module}
                                        data={listData}
                                        loading={loading}
                                        totalRows={totalRows}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
